import { useCallback, useEffect, useState } from 'react';
import type {
  ListItemField,
  ListItemFieldsFilterProps,
} from '../../List/filters/ListItemFieldsFilter/ListItemFieldsFilter.tsx';
import type { TransferListBaseItem } from '../types.ts';
import { readSavedFieldsConfig, updateSavedFieldsConfig } from './utils.ts';

type UseTransferListItemFieldsArgs<TItem extends TransferListBaseItem> = {
  config: ListItemFieldsFilterProps<TItem>['fields'];
  localStorageKey: string;
};

export const useTransferListItemFields = <TItem extends TransferListBaseItem>({
  config,
  localStorageKey,
}: UseTransferListItemFieldsArgs<TItem>) => {
  const [fieldsConfig, setFieldsConfig] = useState<ListItemField<TItem>[]>([]);

  const handleFieldFilterClick = useCallback(
    (id: string) => {
      setFieldsConfig(prev => {
        const newFields = prev.map(field => (field.id === id ? { ...field, isHidden: !field.isHidden } : field));

        updateSavedFieldsConfig(newFields, localStorageKey);

        return newFields;
      });
    },
    [localStorageKey]
  );

  const handleFieldFilterOrderChange = useCallback(
    (newFields: ListItemField<TItem>[]) => {
      updateSavedFieldsConfig(newFields, localStorageKey);
      setFieldsConfig(newFields);
    },
    [localStorageKey]
  );

  useEffect(() => {
    setFieldsConfig(readSavedFieldsConfig(config, localStorageKey));
  }, [config, localStorageKey]);

  return {
    fieldsConfig,
    handleFieldFilterClick,
    handleFieldFilterOrderChange,
  };
};
